import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sun, Moon, SunMoon, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  getMode,
  setMode as persistMode,
  resolveTheme,
  applyTheme,
  getCoords,
  requestGeo,
} from "@/lib/theme";

const OPTIONS = [
  { value: "light", label: "Light", hint: "Always daylight", Icon: Sun },
  { value: "dark", label: "Dark", hint: "Always night", Icon: Moon },
  { value: "auto", label: "Auto", hint: "Follows your sunrise & sunset", Icon: SunMoon },
];

const renderTriggerIcon = (mode) => {
  if (mode === "light") return <Sun size={16} />;
  if (mode === "dark") return <Moon size={16} />;
  return <SunMoon size={16} />;
};

// Theme switcher: Light / Dark / Auto. Auto tracks the real sun for the
// device's location (falls back to time-of-day) and re-checks every minute so
// it flips itself at sunset without a reload.
export const ThemeToggle = ({ className = "" }) => {
  const [mode, setMode] = useState(getMode);
  const tickRef = useRef(null);

  const sync = useCallback((m) => applyTheme(resolveTheme(m)), []);

  // Apply on mount + whenever the mode changes; manage the auto re-check timer.
  // We deliberately do NOT prompt for geolocation here — a default-auto visitor
  // should never see a surprise location prompt. Auto uses cached coords if we
  // already have them, otherwise it silently falls back to time-of-day.
  useEffect(() => {
    sync(mode);
    if (tickRef.current) clearInterval(tickRef.current);
    if (mode === "auto") {
      tickRef.current = setInterval(() => sync("auto"), 60 * 1000);
    }
    return () => tickRef.current && clearInterval(tickRef.current);
  }, [mode, sync]);

  const choose = (value) => {
    persistMode(value);
    setMode(value);
    // Only ask for location when the user *explicitly* opts into Auto and we
    // don't already have coords — that's the moment the intent is clear.
    if (value === "auto" && !getCoords()) {
      requestGeo().then(() => sync("auto"));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Theme"
          title="Theme: light, dark or auto"
          data-testid="theme-toggle-button"
          className={`inline-flex items-center justify-center h-9 w-9 rounded-sm border border-white/10 bg-[#0A192F] text-cyan-300 hover:text-white hover:border-cyan-400/40 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400/50 ${className}`}
        >
          {renderTriggerIcon(mode)}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56 bg-[#0A192F] border-white/10 text-white"
        data-testid="theme-menu"
      >
        <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300">
          Appearance
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        {OPTIONS.map(({ value, label, hint, Icon }) => (
          <DropdownMenuItem
            key={value}
            onClick={() => choose(value)}
            data-testid={`theme-option-${value}`}
            className="flex items-start gap-3 cursor-pointer focus:bg-cyan-400/10 focus:text-white"
          >
            <Icon size={16} className="mt-0.5 text-cyan-300 shrink-0" />
            <span className="flex-1">
              <span className="block text-sm font-semibold">{label}</span>
              <span className="block text-xs text-slate-400">{hint}</span>
            </span>
            {mode === value && <Check size={15} className="mt-0.5 text-cyan-300 shrink-0" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ThemeToggle;
